"use client";

import { sanitizeSecurityDisplayName } from "@lattice/core-lib/utils/security-name";
import AnalystRecommendations from "@lattice/frontend/components/charts/analyst-recommendations";
import ReadMore from "@lattice/frontend/components/others/read-more";
import EtfSectorChart from "@lattice/frontend/components/stock-dashboard/etf-sector-chart";
import InlineSummary from "@lattice/frontend/components/stock-dashboard/inline-summary";
import StockNewsSection from "@lattice/frontend/components/stock-dashboard/stock-news-section";
import StockPriceChartSection from "@lattice/frontend/components/stock-dashboard/stock-price-chart-section";
import StockStatisticsSection from "@lattice/frontend/components/stock-dashboard/stock-statistics-section";
import { Skeleton } from "@lattice/frontend/components/ui/skeleton";
import {
  cardGridGap,
  cardPadding,
  compactCardPadding,
  contentGroupSpacing,
  sectionContainerClass,
  sectionHeaderSpacing,
  sectionHeadingClass,
  sectionSeparatorLineClass,
  sectionSeparatorSpacing,
  separatorLineClass,
  subsectionHeadingClass,
} from "@lattice/frontend/lib/styles/common";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import { routes } from "@lattice/frontend/lib/routes/routes";
import { cn, gothamFonts } from "@lattice/frontend/lib/utils/ui";
import Link from "next/link";
import { Suspense, useEffect } from "react";
import NotFound from "../others/not-found";
import {
  MixpanelEvents,
  trackEvent,
} from "@lattice/frontend/lib/analytics/track";
import EtfStockMakeUpTable from "./etf-stock-make-up-table";
import StockTags from "./stock-tags";
import TradingTrendsSection from "./trading-trends-section";
import { StatsFollowupButtons } from "./stats-followup-buttons";

export default function StockPageClient({
  stock_symbol,
}: {
  stock_symbol: string;
}) {
  const [profile_data] = trpcApi.stock.get_profile.useSuspenseQuery({
    symbol: stock_symbol,
  });
  const [etf_info] = trpcApi.stock.get_etf_info.useSuspenseQuery({
    symbol: stock_symbol,
  });
  const [stock_details] = trpcApi.stock.get_details.useSuspenseQuery({
    symbol: stock_symbol,
  });
  const [peers] = trpcApi.stock.get_peers.useSuspenseQuery({
    symbol: stock_symbol,
  });

  // eslint-disable-next-line no-restricted-syntax -- Fire-and-forget analytics event on stock page view
  useEffect(() => {
    trackEvent(MixpanelEvents.STOCK_VIEWED, { symbol: stock_symbol });
  }, [stock_symbol]);

  if (!stock_details) return <NotFound redirect_link="/app" />;

  return (
    <div className="bg-background min-h-full">
      {/* Overview Section */}
      <div id="overview" className={cn(sectionContainerClass, "bg-background")}>
        {/* Price and Chart Section */}
        <div className={cn("w-full", contentGroupSpacing)}>
          <StockPriceChartSection
            stock_symbol={stock_symbol}
            is_crypto={stock_details.is_crypto}
          />
        </div>

        {/* Price Summary - moved after chart */}
        <InlineSummary
          section="overview"
          isVisible={true}
          symbol={stock_symbol}
          is_crypto={stock_details.is_crypto}
        />

        {/* Company Profile */}
        {profile_data && !stock_details.is_crypto ? (
          <div>
            <div className={cn(contentGroupSpacing, separatorLineClass)} />
            <h3 className={cn(subsectionHeadingClass, "mb-4")}>About</h3>
            <ReadMore
              className="text-foreground/90 text-sm sm:text-base"
              text={profile_data.description}
              threshold={200}
            />

            {/* Tags Section */}
            <div className="mt-6 mb-6">
              <StockTags symbol={stock_symbol} />
            </div>

            {etf_info ? (
              <div
                className={cn(
                  "grid grid-cols-2 lg:grid-cols-5 mt-6",
                  cardGridGap,
                )}
              >
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    EXPENSE RATIO
                  </p>
                  <p className="text-sm sm:text-base">
                    {etf_info.expenseRatio.toFixed(5)}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    ASSETS UNDER MANAGEMENT
                  </p>
                  <p className="text-sm sm:text-base">
                    {new Intl.NumberFormat("en-US", {
                      notation: "compact",
                      compactDisplay: "short",
                    }).format(etf_info.assetsUnderManagement)}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    AVERAGE VOLUME
                  </p>
                  <p className="text-sm sm:text-base">
                    {etf_info.avgVolume === null
                      ? "--"
                      : new Intl.NumberFormat("en-US", {
                          notation: "compact",
                          compactDisplay: "short",
                        }).format(etf_info.avgVolume)}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    NAV
                  </p>
                  <p className="text-sm sm:text-base">
                    $
                    {new Intl.NumberFormat("en-US", {
                      notation: "compact",
                      compactDisplay: "long",
                    }).format(etf_info.nav)}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    NUMBER OF HOLDINGS
                  </p>
                  <p className="text-sm sm:text-base">
                    {new Intl.NumberFormat("en-US", {
                      notation: "compact",
                      compactDisplay: "long",
                    }).format(etf_info.holdingsCount)}
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  "grid grid-cols-2 lg:grid-cols-4 mt-6",
                  cardGridGap,
                )}
              >
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    CEO
                  </p>
                  <p className="text-sm sm:text-base">
                    {profile_data.ceo || "--"}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    INDUSTRY
                  </p>
                  <p className="text-sm sm:text-base">
                    {profile_data.industry || "--"}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    HEADQUARTERS
                  </p>
                  <p className="text-sm sm:text-base">
                    {profile_data.city ? profile_data.city + ", " : ""}
                    {profile_data.country}
                  </p>
                </div>
                <div
                  className={cn(
                    "bg-grey-7 rounded-lg border border-[#131314] space-y-2",
                    compactCardPadding,
                  )}
                >
                  <p
                    className="text-xs font-normal text-muted-foreground/80 uppercase tracking-wider"
                    style={gothamFonts.sidebarSubheader}
                  >
                    MARKET CAP
                  </p>
                  <p className="text-sm sm:text-base">
                    ${" "}
                    {new Intl.NumberFormat("en-US", {
                      notation: "compact",
                      compactDisplay: "long",
                    }).format(profile_data.marketCap)}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {stock_details.is_etf ? (
        <div className={cn(sectionContainerClass, "bg-background")}>
          <h2
            id="sectors"
            className={cn(
              sectionHeadingClass,
              sectionHeaderSpacing,
              "scroll-mt-32",
            )}
          >
            Sectors
          </h2>
          <div
            className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)}
          />
          <div
            className={cn(
              "bg-grey-7 rounded-lg border border-[#131314]",
              cardPadding,
            )}
          >
            <Suspense fallback={<Skeleton className="w-full h-72 lg:h-96" />}>
              <EtfSectorChart symbol={stock_symbol} />
            </Suspense>
          </div>
        </div>
      ) : null}

      {stock_details.is_etf ? (
        <div className={cn(sectionContainerClass, "bg-background")}>
          <h2
            id="stock-make-up"
            className={cn(
              sectionHeadingClass,
              sectionHeaderSpacing,
              "scroll-mt-32",
            )}
          >
            Stock Make Up
          </h2>
          <InlineSummary
            section="etf-holdings"
            isVisible
            symbol={stock_symbol}
            is_crypto={stock_details.is_crypto}
          />
          <div
            className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)}
          />
          <div
            className={cn(
              "bg-grey-7 rounded-lg border border-[#131314]",
              cardPadding,
            )}
          >
            <Suspense fallback={<Skeleton className="w-full h-72" />}>
              <EtfStockMakeUpTable symbol={stock_symbol} />
            </Suspense>
          </div>
        </div>
      ) : null}

      {!stock_details.is_etf && !stock_details.is_crypto ? (
        <StockStatisticsSection symbol={stock_symbol} />
      ) : null}

      {/* Analysts Section */}
      {!stock_details.is_etf && !stock_details.is_crypto ? (
        <div className={cn(sectionContainerClass, "bg-background")}>
          <h2
            id="analysts"
            className={cn(
              sectionHeadingClass,
              sectionHeaderSpacing,
              "scroll-mt-32",
            )}
          >
            Analysts
          </h2>
          <div
            className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)}
          />

          <InlineSummary
            section="analysts"
            isVisible={true}
            symbol={stock_symbol}
            is_crypto={stock_details.is_crypto}
          />

          <AnalystRecommendations
            spec={{
              symbol: stock_symbol,
              type: "Analyst Recommendations",
            }}
          />
        </div>
      ) : null}

      {/* Peers Section */}
      {peers.length ? (
        <div className={cn(sectionContainerClass, "bg-background")}>
          <h2 className={cn(sectionHeadingClass, sectionHeaderSpacing)}>
            Peers
          </h2>
          <div
            className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)}
          />
          <ul
            className={cn(
              "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
              cardGridGap,
            )}
          >
            {peers.map((peer) => (
              <li
                key={peer.symbol}
                className={cn(
                  "rounded-lg flex bg-grey-7 gap-4 items-center border border-[#131314]",
                  compactCardPadding,
                )}
              >
                <div className="bg-secondary rounded-md p-2 min-w-12 min-h-12 w-12 h-12">
                  {peer.image ? (
                    <img
                      className="w-full h-full"
                      alt={`${peer.name} Logo`}
                      src={`data:image/png;base64,${peer.image}`}
                    />
                  ) : (
                    <div className="bg-secondary text-secondary-foreground font-bold w-full h-full rounded-md flex items-center justify-center text-xl">
                      {peer.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="grow flex flex-col">
                  <Link
                    prefetch={false}
                    href={routes.stock(peer.symbol)}
                    className="font-bold text-lg hover:underline"
                  >
                    {peer.symbol}
                  </Link>
                  <p className="text-muted-foreground font-light text-xs">
                    {sanitizeSecurityDisplayName(peer.name)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={cn(sectionContainerClass, "bg-background")}>
        <h2
          className={cn(
            sectionHeadingClass,
            sectionHeaderSpacing,
            "scroll-mt-32",
          )}
          id="news"
        >
          News
        </h2>
        <div
          className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)}
        />

        <InlineSummary
          section="news"
          isVisible={true}
          symbol={stock_symbol}
          is_crypto={stock_details.is_crypto}
        />
        <Suspense
          fallback={
            <div className={cn("grid grid-cols-1 lg:grid-cols-2", cardGridGap)}>
              {Array.from({ length: 6 }, (_, i) => i).map((n) => (
                <Skeleton key={n} className="w-full h-28 rounded-lg" />
              ))}
            </div>
          }
        >
          <StockNewsSection
            symbol={stock_symbol}
            is_crypto={stock_details.is_crypto}
          />
        </Suspense>
      </div>

      {!stock_details.is_etf && !stock_details.is_crypto ? (
        <div className={cn(sectionContainerClass, "bg-background")}>
          <h2
            id="trading-trends"
            className={cn(
              sectionHeadingClass,
              sectionHeaderSpacing,
              "scroll-mt-32",
            )}
          >
            Trading Trends
          </h2>
          <div
            className={cn(sectionSeparatorSpacing, sectionSeparatorLineClass)}
          />
          <InlineSummary
            section="trading-trends"
            isVisible={true}
            symbol={stock_symbol}
            is_crypto={stock_details.is_crypto}
          />
          <TradingTrendsSection symbol={stock_symbol} />
        </div>
      ) : null}

      {/* Stats Followup Questions */}
      {!stock_details.is_etf && !stock_details.is_crypto ? (
        <div className="py-4 px-2 sm:py-6 sm:px-3 mb-6 sm:mb-8 bg-background">
          <div className="border-t border-border/30 mb-6" />
          <Suspense fallback={null}>
            <StatsFollowupButtons symbol={stock_symbol} />
          </Suspense>
        </div>
      ) : null}
    </div>
  );
}
